import { camelizeKeys } from "humps";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import pick from "lodash/pick";
import { isGroupOption } from "PFApp/booking/components/booking_form/workforce_member_select/workforce_member_select.utils";
import { useBookingActivityContext } from "PFApp/booking/parts/providers/booking_activity_context_provider";
import { EngagementItem, type EngagementOption } from "PFApp/components/engagement_item";
import { TemplateKey } from "PFApp/constants/templates";
import { useTemplate } from "PFApp/hooks";
import useBookingModulePermissions from "PFApp/use_booking_module_permissions";
import { LoadingDots } from "PFComponents/loading_dots";
import { Select, SelectProps } from "PFComponents/select";
import { utc } from "PFCore/helpers/date";
import { getAttribute } from "PFCore/helpers/get_attribute";
import { useCurrentAccount } from "PFCore/hooks/queries/account";
import { useActivity } from "PFCore/hooks/queries/activity";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { fetchActivitiesOptions } from "PFCore/services/activities";
import { AccessLevel, Activity, ActivityOption, BookingCategory, DateRangePair, Id, Template } from "PFTypes";
import { Option } from "PFTypes/options";
import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, FieldPath, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { usePrevious, useUnmount } from "react-use";

import { BookingDataItemLimitation, BookingFormMode } from "../booking_form.types";
import { isEditMode } from "../booking_form.utils";
import { truncateTitle } from "../booking_form_common_inputs.utils";
import { BookingFormValues } from "../booking_form_provider";
import { BookingType } from "../booking_type";
import { EngagementPlaceholder } from "./engagement_placeholder";

const ENGAGEMENT_SELECTOR_MIN_HEIGHT = 71; // the height of the selector when an option is selected (to prevent flickering the layout)

const parseEngagementToOption = (activity: ActivityOption): EngagementOption => ({
  id: Number(activity.id),
  name: activity.name,
  description: activity.description
});

type UpdateTitleAndCategory = {
  titleKey: FieldPath<BookingFormValues>;
  categoryKey: FieldPath<BookingFormValues>;
  oldTitle: string | undefined;
  oldCategory: BookingCategory | null | undefined;
  selectedActivity: Activity;
};

type EngagementSelectorProps = {
  mode: BookingFormMode;
};

export const EngagementSelector = ({ mode }: EngagementSelectorProps) => {
  const { t } = useTranslation(["translation", "activities", "core", "bookingModule"]);
  const {
    data: {
      config: { minimum_search_term_length: minimumSearchTermLength }
    }
  } = useCurrentAccount();
  const {
    data: { id: currentProfileId }
  } = useCurrentProfile();
  const { activity, isLoading } = useBookingActivityContext();
  const engagementTemplate = useTemplate(TemplateKey.Engagement as Template["key"]);
  const { getBookingCategory } = useBookingCategories();
  const { formatISODate } = useDateFormatter();

  const previousDuration = useRef<DateRangePair>({
    startDate: formatISODate(utc()),
    endDate: formatISODate(utc())
  });

  const { canManagePublicActivityBookings } = useBookingModulePermissions();
  const prevEngagementData = useRef<{ name: string | null; bookingCategoryId: Id | null }>({
    name: null,
    bookingCategoryId: null
  });

  const isInitialActivityEngagement = activity && engagementTemplate?.id === activity.template_id;
  const initialEngagementId = useRef<Id | undefined>(isInitialActivityEngagement ? activity?.id : undefined);
  const [selectedEngagement, setSelectedEngagement] = useState<ActivityOption | null>(null);
  const { control, setValue } = useFormContext<BookingFormValues>();
  const [globalTitle, bookingType, globalCategory, workforceMember, bookings, activityId] = useWatch<
    BookingFormValues,
    ["globalTitle", "bookingType", "globalCategory", "workforceMember", "bookings", "activityId"]
  >({
    name: ["globalTitle", "bookingType", "globalCategory", "workforceMember", "bookings", "activityId"]
  });
  const isRepeated = bookingType === BookingType.Repeated;
  const prevWorkforceMember = usePrevious(workforceMember);

  const { data: selectedActivity, isFetching: isFetchingActivity } = useActivity(activityId!, {
    enabled: !!activityId,
    staleTime: Infinity
  });

  const updateTitleAndCategory = ({
    titleKey,
    categoryKey,
    oldTitle,
    oldCategory,
    selectedActivity
  }: UpdateTitleAndCategory) => {
    const prevCategory = getBookingCategory(prevEngagementData.current.bookingCategoryId);
    const newCategory = getBookingCategory(getAttribute(selectedActivity, "booking_category_id"));

    const isTitlePreviousEngagementName = oldTitle === prevEngagementData.current.name;
    const isTitlePreviousCategoryName = oldTitle === getAttribute(prevCategory, "displayAs");
    const isCategoryPreviousEngagementCategory =
      prevEngagementData.current.bookingCategoryId === oldCategory?.id;

    if (!oldTitle || isTitlePreviousEngagementName || isTitlePreviousCategoryName) {
      const newTitle = selectedActivity.private
        ? getAttribute(newCategory, "displayAs")
        : selectedActivity.name;
      setValue(titleKey, truncateTitle(newTitle));
    }
    if (!isNil(newCategory) && (!oldCategory || isCategoryPreviousEngagementCategory)) {
      const isPermittedToWrite = getAttribute(newCategory, "bookingsPermissions") === AccessLevel.ReadWrite;
      isPermittedToWrite &&
        setValue(categoryKey, newCategory, {
          shouldValidate: true
        });
    }
  };

  const handleChange = (engagementOption: ActivityOption | null, onChange: (value: any) => void) => {
    setSelectedEngagement(engagementOption);
    onChange(engagementOption?.id);
  };

  useEffect(() => {
    if (isFetchingActivity) {
      return;
    }
    if (!selectedActivity) {
      prevEngagementData.current = {
        name: null,
        bookingCategoryId: null
      };
      return;
    }

    if (mode === BookingFormMode.Create) {
      updateTitleAndCategory({
        selectedActivity,
        oldTitle: globalTitle,
        oldCategory: globalCategory,
        categoryKey: "globalCategory",
        titleKey: "globalTitle"
      });
    } else {
      bookings.forEach((booking, index) => {
        updateTitleAndCategory({
          selectedActivity,
          oldTitle: booking.title,
          oldCategory: booking.category,
          categoryKey: `bookings.${index}.category`,
          titleKey: `bookings.${index}.title`
        });
      });
    }
    prevEngagementData.current = {
      name: selectedActivity.name,
      bookingCategoryId: getAttribute(selectedActivity, "booking_category_id") ?? null
    };
  }, [selectedActivity, isFetchingActivity]);

  useEffect(() => {
    if (!selectedActivity || !bookings || bookings.length !== 1) {
      return;
    }

    const isDurationDirty =
      bookings[0]?.startDate !== previousDuration.current.startDate ||
      bookings[0]?.endDate !== previousDuration.current.endDate;

    if (isDurationDirty) {
      return;
    }

    const duration = selectedActivity.metadata?.duration;

    if (duration?.start_date && duration?.end_date) {
      const startDate = formatISODate(duration.start_date);
      const endDate = formatISODate(duration.end_date);

      setValue(`bookings.0.startDate`, startDate);
      setValue(`bookings.0.endDate`, endDate);
      previousDuration.current = {
        startDate,
        endDate
      };
    } else {
      const today = formatISODate(utc());

      previousDuration.current = {
        startDate: today,
        endDate: today
      };
    }
  }, [selectedActivity]);

  useEffect(() => {
    setSelectedEngagement(
      isInitialActivityEngagement
        ? (pick(
            camelizeKeys(activity),
            "id",
            "name",
            "description",
            "bookingCategoryId",
            "templateId"
          ) as ActivityOption)
        : null
    );
  }, [activity]);

  useEffect(() => {
    const prevSelectedProfileId =
      prevWorkforceMember && "id" in prevWorkforceMember ? prevWorkforceMember.id : undefined;
    const newSelectedProfileId = workforceMember && "id" in workforceMember ? workforceMember.id : undefined;
    if (prevSelectedProfileId === currentProfileId && newSelectedProfileId !== currentProfileId) {
      // activity is removed in case user changes from current profile to other
      // as there might be less available activities (ownerId vs contributorId filtering)
      setValue("activityId", undefined, { shouldValidate: true });
      setSelectedEngagement(null);
    }
  }, [workforceMember]);

  useUnmount(() => setValue("activityId", undefined));

  const getOwnershipFilters = () => {
    // if users book themselves, they need to be contributor unless public engagements policy is enabled
    // if users book other profiles, they need to be owner unless public engagements policy is enabled
    const isGroupSelected = isGroupOption(workforceMember);
    if (!isGroupSelected && workforceMember?.id === currentProfileId) {
      return { contributorId: canManagePublicActivityBookings ? undefined : currentProfileId };
    }
    return { ownerId: canManagePublicActivityBookings ? undefined : currentProfileId };
  };

  const { ownerId, contributorId } = getOwnershipFilters();

  const minTermLength = minimumSearchTermLength.activities ?? 3;
  const fetchOptions = useCallback<NonNullable<SelectProps<ActivityOption>["fetchOptions"]>>(
    async ({ ids, searchTerm }) => {
      if (isEmpty(ids) && (!searchTerm || searchTerm.length < minTermLength)) {
        return { entries: [], meta: { total: 0 } };
      }
      const response = await fetchActivitiesOptions({
        id: ids,
        term: searchTerm ?? "",
        templateKey: engagementTemplate?.key,
        state: ["draft", "new"],
        ...(!isNil(ownerId) ? { owned: true } : {}),
        ...(!isNil(contributorId) ? { contributor: true } : {})
      });
      const options = response.entries.map((entry) => ({
        id: entry.id,
        value: entry.name,
        displayElement: <EngagementItem item={parseEngagementToOption(entry)} />,
        original: entry
      }));
      return {
        ...response,
        entries: options
      };
    },
    [engagementTemplate?.key, ownerId, contributorId, minTermLength]
  );

  if (isLoading) {
    return <LoadingDots />;
  }

  if (isRepeated) {
    return <EngagementPlaceholder />;
  }

  const getNoOptionsText = (searchTerm: string) => {
    if (!searchTerm || (minTermLength > 1 && searchTerm.length < minTermLength)) {
      return t("translation:typeNCharactersToGetSuggestions", { count: minTermLength });
    } else {
      return t("core:components.select.noItemsToShow");
    }
  };

  const letClear = !isInitialActivityEngagement || !isEditMode(mode);
  const selectedEngagementId = selectedEngagement?.id ? Number(selectedEngagement.id) : undefined;
  const bookingsToBeAffected =
    bookings?.filter((booking) => booking.limitation === BookingDataItemLimitation.None) ?? [];
  const isEngagementChangedWarningVisible =
    !!selectedEngagement?.id &&
    selectedEngagement.id !== initialEngagementId.current &&
    bookingsToBeAffected.length > 1;

  return (
    <div style={{ minHeight: selectedEngagement ? ENGAGEMENT_SELECTOR_MIN_HEIGHT : undefined }}>
      <Controller
        name="activityId"
        control={control}
        render={({ field }) => (
          <Select<ActivityOption>
            label={t(`activities:templates.engagement`)}
            dropdownId={`engagement-selector-coowned-${ownerId}-${contributorId}`}
            renderDisplayValue={(value) => value?.displayElement}
            fetchOptions={fetchOptions}
            value={selectedEngagementId}
            onChange={(option: Option<ActivityOption>) => handleChange(option.original, field.onChange)}
            onClear={
              letClear && !isNil(selectedEngagementId) ? () => handleChange(null, field.onChange) : undefined
            }
            noOptionsText={getNoOptionsText}
            warning={
              isEngagementChangedWarningVisible
                ? t("bookingModule:bookings.update.engagementWillBeAppliedToBookingsWarning", {
                    count: bookingsToBeAffected.length
                  })
                : undefined
            }
          />
        )}
      />
    </div>
  );
};
